import {create} from 'zustand';
import {getUrlParams} from '@/utils';
import {
  getHomeGames,
  getKeralaList,
  getMatkaList,
  getP11MatchList,
} from '../home.service';
import {
  getGameProvider,
  getGoldAreaList,
  getLiveHotGameList,
  LiveHotGameItem,
  postNewGameList,
  Provider,
} from './service';
import {
  NewGameListItem,
  NewGameListParams,
} from '@/common-pages/casino/casino.service';
import {
  DigitListItem,
  GoldAreaItem,
  HomeGameFloorItem,
  KeralaListItem,
  MatkaListItem,
  P11MatchItem,
} from '../home.type';

const buildGameTypesList = (list: GoldAreaItem[] = []) => {
  return list || [];
};

const getSelectedGameTypeFromGoldArea = (item?: GoldAreaItem) => {
  if (!item) {
    return '';
  }

  return item.moduleType;
};

type HomeStoreState = {
  selectedProvider: string;
  selectedGameType: string;
  casinoProviders: Provider[];
  gameTypes: HomeGameFloorItem[];
  keralaList: KeralaListItem[];
  digitList: DigitListItem[];
  matkaList: MatkaListItem[];
  pick11List: P11MatchItem[];
  hotGameList: LiveHotGameItem[];
  gameTypesList: GoldAreaItem[];
};

type HomeStoreActions = {
  getProviders: (type: 'casino' | 'liveCasino') => Promise<void>;
  fetchCasinoGames: (params: {
    page: number;
    pageSize: number;
  }) => Promise<NewGameListItem[]>;
  handleProviderChange: (provider: string) => void;
  getLotteryGames: () => Promise<void>;
  handleGameTypeChange: (gameType: string) => void;
  getHotGames: () => Promise<void>;
  getGameTypesList: () => Promise<void>;
};

const filterGames = ['casino', 'liveCasino', 'scratch'];

const filterHomeGameTypes = (homeGames: HomeGameFloorItem[] = []) =>
  homeGames.filter(
    item =>
      !filterGames.includes(item.uniqueKey) ||
      (item.gamesList && item.gamesList.length > 0),
  );

const getDefaultGameType = (): string => {
  const urlParams = getUrlParams();
  const gameParam = urlParams.game;
  return gameParam === 'casino' ? 'CASINO' : 'LOTTERY';
};

export const useHomeStore = create<HomeStoreState & HomeStoreActions>(
  (set, get) => ({
    gameTypes: [],
    keralaList: [],
    digitList: [],
    matkaList: [],
    pick11List: [],
    casinoProviders: [],
    selectedProvider: '0',
    selectedGameType: getDefaultGameType(),
    hotGameList: [],
    gameTypesList: [],

    handleProviderChange: provider => {
      set({selectedProvider: provider});
    },
    handleGameTypeChange: (gameType: string) => {
      set({selectedGameType: gameType});
      set({selectedProvider: '0'});
    },
    getProviders: async (type: 'casino' | 'liveCasino') => {
      const res = await getGameProvider(type);
      res.unshift({
        id: 0,
        name: 'Hot',
        logo: '/assets/images/providerIcon/Hot.webp',
      });
      set({casinoProviders: res, selectedProvider: res[0].id.toString()});
    },
    fetchCasinoGames: async ({page, pageSize}) => {
      const {selectedGameType, selectedProvider} = get();
      const params: NewGameListParams = {
        l1CategoryId: selectedGameType === 'CASINO' ? 2 : 3,
        l2CategoryId: '',
        pageNo: page,
        providerId: selectedProvider === '0' ? '' : selectedProvider,
        pageSize,
      };
      const res = await postNewGameList(params);
      return res.content || [];
    },
    getLotteryGames: async () => {
      try {
        const homeGames = await getHomeGames();
        set({
          gameTypes: filterHomeGameTypes(homeGames),
        });
      } catch {}

      getKeralaList()
        .then(keralaList => {
          set({keralaList});
        })
        .catch(() => undefined);

      getMatkaList()
        .then(matkaList => {
          set({matkaList});
        })
        .catch(() => undefined);

      getP11MatchList()
        .then(pick11Response => {
          set({pick11List: pick11Response?.matches || []});
        })
        .catch(() => undefined);
    },
    getHotGames: async () => {
      const res = await getLiveHotGameList();
      set({
        hotGameList: res,
      });
    },
    getGameTypesList: async () => {
      try {
        const res = await getGoldAreaList();
        const gameTypesList = buildGameTypesList(res);
        set({
          gameTypesList,
          selectedGameType: getSelectedGameTypeFromGoldArea(gameTypesList[0]),
          selectedProvider: '0',
        });
      } catch {
        set({
          gameTypesList: [],
          selectedGameType: '',
          selectedProvider: '0',
        });
      }
    },
  }),
);
